// cspell:disable
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  LoadState,
  useLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import ThreeDotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward_filled.svg";
import {
  FulfillmentGroupData,
  uniqueOrderNames,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { PickSession } from "@redotech/redo-model/wms/schema/pick-session";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { OptionalTooltip } from "@redotech/redo-web/tooltip/optional-tooltip";
import { memo, useMemo, useState } from "react";
import { FulfillmentGroupStatusBadge } from "../common/status-badge";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

enum OrderAction {
  MarkAsProblem = "mark_as_problem",
  ReleaseFromPickSession = "release_from_pick_session",
}

interface OrderSummaryItem {
  imageUrl: string;
  productTitle: string;
  variantTitle: string;
  quantity: number;
}

interface OrderSummary {
  fulfillmentGroupId: string;
  orderNames: string[];
  customerName: string | null;
  items: OrderSummaryItem[];
  totalQuantity: number;
  status: FulfillmentGroupStatus | null;
}

export const PickSessionOrdersModal = memo(function PickSessionOrdersModal({
  isOpen,
  onClose,
  pickSession,
  onPickSessionUpdate,
  fulfillmentGroupIdInView,
  handleNavigate,
}: {
  isOpen: boolean;
  onClose: () => void;
  pickSession: PickSession;
  onPickSessionUpdate: (pickSession: PickSession) => void;
  fulfillmentGroupIdInView: string | null | undefined;
  handleNavigate: (value: string) => void;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  // Get unique fulfillment group IDs from pick session items
  const fulfillmentGroupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of pickSession.items) {
      ids.add(item.fulfillmentGroupId);
    }
    return Array.from(ids);
  }, [pickSession.items]);

  // Fetch fulfillment group data to get order numbers and statuses
  // Refetch when modal opens to ensure fresh data
  const fulfillmentGroupsLoad = useLoad(async () => {
    if (!isOpen) {
      return [];
    }
    const { replacements } = await rpcClient.getReplacementGroups({
      groupIds: fulfillmentGroupIds,
    });
    return replacements.map((r) => r.replacementGroup);
  }, [rpcClient, fulfillmentGroupIds, isOpen]);

  // Build order summaries
  const orderSummaries = useMemo((): OrderSummary[] => {
    const groupMap = new Map<string, FulfillmentGroupData>();
    for (const group of fulfillmentGroupsLoad.value || []) {
      groupMap.set(group._id.toString(), group);
    }

    const summaries: OrderSummary[] = [];
    const itemsByGroup = new Map<string, OrderSummaryItem[]>();

    // Group items by fulfillmentGroupId
    for (const item of pickSession.items) {
      const existing = itemsByGroup.get(item.fulfillmentGroupId) || [];
      existing.push({
        imageUrl: item.imageUrl ?? "",
        productTitle: item.productTitle,
        variantTitle: item.variantTitle,
        quantity: item.totalQuantity,
      });
      itemsByGroup.set(item.fulfillmentGroupId, existing);
    }

    // Build summaries
    for (const [groupId, items] of itemsByGroup) {
      const group = groupMap.get(groupId);
      const orderNames = group
        ? uniqueOrderNames(group)
        : [groupId.slice(-8).toUpperCase()];

      summaries.push({
        fulfillmentGroupId: groupId,
        orderNames,
        customerName: group?.customerName ?? null,
        items,
        totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
        status: group?.status ?? null,
      });
    }

    return summaries;
  }, [pickSession.items, fulfillmentGroupsLoad.value]);

  const handleMarkAsProblem = useHandler(async (fulfillmentGroupId: string) => {
    const result = await rpcClient.markFulfillmentGroupAsProblem({
      pickSessionId: pickSession._id.toString(),
      fulfillmentGroupId,
      pickSessionItemStatuses: undefined,
    });
    if (result.success) {
      toast("Order marked not picked", { variant: "success" });
      if (result.updatedPickSession) {
        onPickSessionUpdate(result.updatedPickSession);
      }
      // Close the modal after successful mark as problem
      onClose();
    } else {
      toast(result.message || "Failed to mark order as problem", {
        variant: "error",
      });
    }
  });

  const [actionLoad, triggerAction] = useTriggerLoadWithInput(
    async ({
      fulfillmentGroupId,
      action,
    }: {
      fulfillmentGroupId: string;
      action: OrderAction;
    }) => {
      try {
        if (action === OrderAction.MarkAsProblem) {
          await handleMarkAsProblem(fulfillmentGroupId);
        } else if (action === OrderAction.ReleaseFromPickSession) {
          const result = await rpcClient.cancelPicking({ fulfillmentGroupId });
          if (result.success) {
            toast("Order released from pick session", { variant: "success" });
            // Remove items from pick session locally
            const updatedItems = pickSession.items.filter(
              (item) => item.fulfillmentGroupId !== fulfillmentGroupId,
            );
            onPickSessionUpdate({ ...pickSession, items: updatedItems });
          } else {
            toast(result.message || "Failed to release order", {
              variant: "error",
            });
          }
        }
      } catch (error) {
        toast(`Failed to perform action: ${(error as Error).message}`, {
          variant: "error",
        });
      }
    },
  );

  return (
    <RedoModal
      hideFooter
      isOpen={isOpen}
      modalSize="lg"
      noPadding
      onModalCloseRequested={onClose}
      subtitle={`${orderSummaries.length} shipment${orderSummaries.length === 1 ? "" : "s"} in this pick session`}
      title="Pick session shipments"
    >
      {orderSummaries.length === 0 && (
        <Flex align="center" justify="center" p="3xl">
          <Text fontSize="sm" textColor="secondary">
            No shipments in this pick session
          </Text>
        </Flex>
      )}
      {fulfillmentGroupsLoad.pending ? (
        <LoadingState />
      ) : (
        <OrderTable
          actionLoad={actionLoad}
          fulfillmentGroupIdInView={fulfillmentGroupIdInView || ""}
          handleNavigate={handleNavigate}
          onClose={onClose}
          orderSummaries={orderSummaries}
          triggerAction={triggerAction}
        />
      )}
    </RedoModal>
  );
});

const LoadingState = memo(function LoadingState() {
  return (
    <Flex
      dir="column"
      gap="md"
      justify="center"
      pb="md"
      pt="none"
      px="lg"
      w="full"
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <AnimatedSkeletonBlock height="60px" key={index} width="100%" />
      ))}
    </Flex>
  );
});

const OrderTable = memo(function OrderTable({
  orderSummaries,
  actionLoad,
  triggerAction,
  fulfillmentGroupIdInView,
  onClose,
  handleNavigate,
}: {
  orderSummaries: OrderSummary[];
  actionLoad: LoadState<void>;
  triggerAction: (input: {
    fulfillmentGroupId: string;
    action: OrderAction;
  }) => void;
  fulfillmentGroupIdInView: string;
  onClose: () => void;
  handleNavigate: (value: string) => void;
}) {
  const sortedByStatus = orderSummaries.sort(
    (a, b) =>
      Number(b.status === FulfillmentGroupStatus.Open) -
      Number(a.status === FulfillmentGroupStatus.Open),
  );

  const handleOrderClick = useHandler((fulfillmentGroupId: string) => {
    handleNavigate(fulfillmentGroupId);
    onClose();
  });

  const columns: RedoTableColumn<OrderSummary>[] = [
    {
      key: "order",
      widthMode: { type: "fixed", width: "200px" },
      alignment: "left",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Order" }),
      renderNormalCell: ({ row }) => {
        const orderNameDisplay = row.orderNames.join(", ");

        const maxLength = 20;
        return (
          <Flex dir="column" gap="xxs" p="xl">
            <OptionalTooltip
              meetsCondition={orderNameDisplay.length > maxLength}
              tooltipProps={{ title: orderNameDisplay }}
            >
              <Text fontSize="sm" textColor="primary">
                {orderNameDisplay.length > maxLength
                  ? orderNameDisplay.slice(0, maxLength) + "..."
                  : orderNameDisplay}
              </Text>
            </OptionalTooltip>
            {row.fulfillmentGroupId === fulfillmentGroupIdInView ? (
              <Text
                fontSize="xs"
                fontStyle="italic"
                fontWeight="thin"
                textColor="tertiary"
              >
                Currently viewing
              </Text>
            ) : (
              <Text
                cursor="pointer"
                fontSize="xs"
                onClick={() => handleOrderClick(row.fulfillmentGroupId)}
                textColor="tertiary"
                textDecoration="underline"
              >
                View order
              </Text>
            )}
          </Flex>
        );
      },
    },
    {
      key: "customer",
      widthMode: { type: "fixed", width: "150px" },
      alignment: "left",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Customer" }),
      renderNormalCell: ({ row }) => {
        const customerName = row.customerName ?? "-";
        const maxLength = 18;
        return (
          <Flex pl="xl">
            <OptionalTooltip
              meetsCondition={customerName.length > maxLength}
              tooltipProps={{ title: customerName }}
            >
              <Text fontSize="sm" textColor="tertiary">
                {customerName.length > maxLength
                  ? customerName.slice(0, maxLength) + "..."
                  : customerName}
              </Text>
            </OptionalTooltip>
          </Flex>
        );
      },
    },
    {
      key: "item",
      alignment: "left",
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Item" }),
      renderNormalCell: ({ row }) => {
        // This table is just for displaying single item orders
        const firstItem = row.items[0];

        return (
          <Flex align="center" dir="row" gap="sm" justify="center" px="xl">
            <img
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              alt={firstItem.productTitle}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              src={firstItem.imageUrl}
              style={{
                width: "32px",
                height: "32px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
            <Text fontSize="sm" textColor="tertiary">
              {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
              {firstItem.variantTitle !== "Default Title"
                ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  `${firstItem.productTitle} - ${firstItem.variantTitle}`
                : // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  firstItem.productTitle}
            </Text>
          </Flex>
        );
      },
    },
    {
      key: "status",
      alignment: "center",
      widthMode: { type: "fixed", width: "100px" },
      renderHeaderCell: () =>
        StandardRedoTableHeaderCell({ headerText: "Status" }),
      renderNormalCell: ({ row }) => (
        <Flex pl="xl">
          <FulfillmentGroupStatusBadge
            fulfillmentGroupStatus={row.status}
            size="sm"
          />
        </Flex>
      ),
    },
    {
      key: "actions",
      alignment: "right",
      widthMode: { type: "fixed", width: "100px" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }) => (
        <OrderActionsCell
          actionLoad={actionLoad}
          row={row}
          triggerAction={triggerAction}
        />
      ),
    },
  ];
  return (
    <RedoTable layout={{ columns }} rowsOfData={sortedByStatus} size="md" />
  );
});

const OrderActionsCell = memo(function OrderActionsCell({
  row,
  actionLoad,
  triggerAction,
}: {
  row: OrderSummary;
  actionLoad: LoadState<void>;
  triggerAction: (input: {
    fulfillmentGroupId: string;
    action: OrderAction;
  }) => void;
}) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <Flex align="center" basis="100px" justify="flex-end" pr="xl" shrink={0}>
      <RedoButton
        disabled={actionLoad.pending}
        hierarchy="tertiary"
        IconLeading={ThreeDotsHorizontalIcon}
        onClick={(e) => {
          e.stopPropagation();
          setDropdownOpen(!dropdownOpen);
        }}
        ref={setDropdownButtonRef}
        size="md"
      />
      <RedoListDropdown<OrderAction>
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor={false}
        items={[
          {
            id: OrderAction.MarkAsProblem,
            value: OrderAction.MarkAsProblem,
            type: "text",
            text: "Item not picked",
          },
          {
            id: OrderAction.ReleaseFromPickSession,
            value: OrderAction.ReleaseFromPickSession,
            type: "custom",
            useWrapper: true,
            render: (
              <>
                <RedoIcon color="red" Icon={FlipBackwardIcon} size={16} />
                <Text color="red" fontSize="xs" fontWeight="medium">
                  Send order to be repicked
                </Text>
              </>
            ),
          },
        ]}
        itemSelected={({ item }) => {
          triggerAction({
            fulfillmentGroupId: row.fulfillmentGroupId,
            action: item.value,
          });
          setDropdownOpen(false);
        }}
        selectedItems={undefined}
        setDropdownOpen={setDropdownOpen}
      />
    </Flex>
  );
});
